import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTutorial } from "@/contexts/TutorialContext";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Sparkles, Loader2, Flame, Clock, RefreshCw, SkipForward, X, Refrigerator, Plus, Undo2, Wallet } from "lucide-react";
import TutorialTooltip from "@/components/TutorialTooltip";
import AddDishModal from "@/components/AddDishModal";
import CookTimeSlider, { COOK_TIME_OPTIONS } from "@/components/CookTimeSlider";
import { toLocalDateStr, formatPriceRange } from "@/lib/utils";
import { MEAL_CATEGORIES, groupMealsByCategory, MEAL_BORDER_COLORS } from "@/lib/mealGrouping";
import { isLocked, isSubActive } from "@/utils/subscription";
import { CrownIcon } from "@/components/LockBadge";
import { plural } from "@/i18n/plural";

export default function MealPlannerPage() {
  const { api, user, updateUser } = useAuth();
  const { t, lang } = useLanguage();
  const { tutorialActive, tutorialStep, updateAppState } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [replacingMeal, setReplacingMeal] = useState(null);
  const [skipModal, setSkipModal] = useState(null);
  const [skipComment, setSkipComment] = useState("");
  const [addDishModal, setAddDishModal] = useState(null); // { mealType }
  // Undo state
  const [undoItems, setUndoItems] = useState({}); // { recipeId: meal }
  const undoTimers = useRef({});
  const [cookTimeIdx, setCookTimeIdx] = useState(4); // default: no limit
  const [dishCosts, setDishCosts] = useState({});   // recipe_id -> відповідь calculate-cost
  // Дзеркало dishCosts для фільтра «вже знаємо». Саме ref, а не стан: інакше
  // fetchDishCosts залежала б від dishCosts, мінялася б після кожної відповіді й
  // тягнула б за собою fetchPlans, яка стоїть у useEffect — вийшов би цикл запитів.
  const knownCosts = useRef({});
  const maxCookTime = COOK_TIME_OPTIONS[cookTimeIdx]; // minutes or null
  const locked = isLocked(user);

  const dateStr = toLocalDateStr(currentDate);
  const dayNamesShort = {
    uk: ["Нд", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
    en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    ru: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
  };

  // Вартість страв. Інгредієнти вже їдуть у страві, тож по рецепт ходити не треба.
  // На відміну від головної, мапа НЕ перезаписується, а доповнюється: планер міняє
  // день на кожен клік по стрічці тижня, і без цього перебір семи днів слав би
  // запити по колу. Ключ recipe_id стабільний, страви між днями повторюються.
  //
  // Уже відомі recipe_id відсіюються ДО запиту — рахуємо лише нові.
  // Невдалу страву в knownCosts не пишемо: наступний захід на цей день спробує ще раз.
  //
  // Оголошено вище за fetchPlans: та тримає цю функцію у своєму масиві залежностей,
  // а він обчислюється під час рендера — на const нижче він упав би в TDZ.
  const fetchDishCosts = useCallback(async (meals) => {
    const list = (meals || []).filter(
      (m) => m?.recipe_id && (m.ingredients || []).length && !(m.recipe_id in knownCosts.current)
    );
    if (!list.length) return;
    const results = await Promise.allSettled(
      list.map((m) => api().post("/recipes/calculate-cost", { ingredients: m.ingredients }))
    );
    const next = {};
    results.forEach((r, i) => {
      if (r.status === "fulfilled") next[list[i].recipe_id] = r.value?.data;
      else console.error(r.reason);
    });
    if (!Object.keys(next).length) return;
    knownCosts.current = { ...knownCosts.current, ...next };
    setDishCosts((prev) => ({ ...prev, ...next }));
  }, [api]);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await api().get(`/meal-plans?date=${dateStr}&lang=${lang}`);
      setPlans(res.data || []);
      // Update tutorial app state
      const hasMeals = res.data?.some(p => p.days?.some(d => d.date === dateStr && d.meals?.length > 0));
      updateAppState({ hasMealPlan: hasMeals, currentPage: "/planner" });
      // Той самий пошук дня, що в currentDayData(), але по свіжій відповіді:
      // стан ще не встиг застосуватись. Не await — план має відрендеритись одразу.
      const day = (res.data || []).flatMap((p) => p.days || []).find((d) => d.date === dateStr);
      fetchDishCosts(day?.meals);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api, dateStr, updateAppState, lang, fetchDishCosts]);

  useEffect(() => { setLoading(true); fetchPlans(); }, [fetchPlans]);

  // Update current page in tutorial context
  useEffect(() => {
    updateAppState({ currentPage: location.pathname });
  }, [location.pathname, updateAppState]);

  // Cleanup undo timers on unmount
  useEffect(() => {
    return () => { Object.values(undoTimers.current).forEach(clearTimeout); };
  }, []);

  const refreshUser = async () => {
    try { const me = await api().get("/auth/me"); updateUser(me.data); } catch (e) {}
  };

  const handleGenerate = async (days) => {
    setGenerating(true);
    setGenError("");
    try {
      // For tutorial: use fixed "Tutorial Recipe" for first generation
      const isTutorialGeneration = tutorialActive && tutorialStep === 1;
      const endpoint = isTutorialGeneration 
        ? "/meal-plans/generate-tutorial"
        : "/meal-plans/generate";
      
      await api().post(endpoint, { date: dateStr, days, language: lang, max_cook_time: maxCookTime }, { redirectOnPaywall: true });
      await fetchPlans();
      await refreshUser();
      localStorage.setItem("mealpill_grocery_stale", "true");
      // State-based progression will handle step advancement automatically via updateAppState
    } catch (err) {
      console.error(err);
      const detail = err?.response?.status === 402 ? err?.response?.data?.detail : null;
      if (detail?.reason === "budget_exhausted") {
        setGenError(detail.remaining === 0
          ? t("planDaysExhausted")
          : t("planDaysInsufficient").replace("{n}", detail.requested));
        await refreshUser();
      } else {
        setGenError(t("genFailed"));
      }
    }
    setGenerating(false);
  };

  const handleGenerateFromFridge = async () => {
    setGenerating(true);
    setGenError("");
    try {
      await api().post("/meal-plans/generate", { date: dateStr, days: 1, fridge_only: true, language: lang, max_cook_time: maxCookTime }, { redirectOnPaywall: true });
      await fetchPlans();
      await refreshUser();
      localStorage.setItem("mealpill_grocery_stale", "true");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.status === 402 ? err?.response?.data?.detail : null;
      if (detail?.reason === "budget_exhausted") {
        setGenError(detail.remaining === 0
          ? t("planDaysExhausted")
          : t("planDaysInsufficient").replace("{n}", detail.requested));
        await refreshUser();
      } else {
        setGenError(t("genFailed"));
      }
    }
    setGenerating(false);
  };

  const handleReplaceMeal = async (planId, mealType) => {
    setReplacingMeal(mealType);
    try {
      await api().post("/meal-plans/replace-meal", { plan_id: planId, date: dateStr, meal_type: mealType }, { redirectOnPaywall: true });
      await fetchPlans();
      localStorage.setItem("mealpill_grocery_stale", "true");
    } catch (err) { console.error(err); }
    setReplacingMeal(null);
  };

  const handleSkipMeal = async (planId) => {
    try {
      await api().post("/meal-plans/skip-meal", { plan_id: planId, date: dateStr, meal_type: skipModal.meal_type, recipe_id: skipModal.recipe_id, comment: skipComment || null });
      await fetchPlans();
      localStorage.setItem("mealpill_grocery_stale", "true");
    } catch (err) { console.error(err); }
    setSkipModal(null);
    setSkipComment("");
  };

  const handleRemoveDish = async (meal) => {
    if (!planId || !meal.recipe_id) return;
    try {
      await api().post("/meal-plans/remove-dish", {
        plan_id: planId, date: dateStr, recipe_id: meal.recipe_id,
      });
      await fetchPlans();
      localStorage.setItem("mealpill_grocery_stale", "true");
      const id = meal.recipe_id;
      setUndoItems((prev) => ({ ...prev, [id]: meal }));
      if (undoTimers.current[id]) clearTimeout(undoTimers.current[id]);
      undoTimers.current[id] = setTimeout(() => {
        setUndoItems((prev) => { const n = { ...prev }; delete n[id]; return n; });
        delete undoTimers.current[id];
      }, 10000);
    } catch (err) { console.error(err); }
  };

  const handleUndo = async (meal) => {
    if (!planId) return;
    const id = meal.recipe_id;
    if (undoTimers.current[id]) clearTimeout(undoTimers.current[id]);
    setUndoItems((prev) => { const n = { ...prev }; delete n[id]; return n; });
    delete undoTimers.current[id];
    try {
      await api().post("/meal-plans/restore-dish", {
        plan_id: planId, date: dateStr,
        meal_type: meal.meal_type, recipe_id: meal.recipe_id,
        recipe_title: meal.recipe_title, recipe_description: meal.recipe_description,
        calories: meal.calories, cook_time: meal.cook_time,
        dish_subtype: meal.dish_subtype, tags: meal.tags || [],
      });
      await fetchPlans();
    } catch (err) { console.error(err); }
  };

  // Add Dish flow - create plan if it doesn't exist for this date
  const openAddDish = async (mealType) => {
    if (!planId) {
      setGenerating(true);
      try {
        const res = await api().post("/meal-plans/create-empty", { date: dateStr });
        if (!res.data?.plan_id) {
          setGenerating(false);
          setGenError(t("genFailed"));
          return;
        }
        await fetchPlans();
      } catch (err) {
        console.error(err);
        setGenerating(false);
        setGenError(t("genFailed"));
        return;
      }
      setGenerating(false);
    }
    setAddDishModal({ mealType });
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    start.setHours(0, 0, 0, 0);
    // Monday-based week: Mon=0, Tue=1, ..., Sun=6
    const dayOfWeek = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - dayOfWeek);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  };

  const goToToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCurrentDate(d);
  };

  const currentDayData = () => {
    for (const plan of plans) {
      for (const day of plan.days || []) {
        if (day.date === dateStr) return { meals: day.meals, planId: plan.id };
      }
    }
    return { meals: [], planId: null };
  };

  const { meals, planId } = currentDayData();
  const groupedMeals = groupMealsByCategory(meals);
  const firstFilledMealType = MEAL_CATEGORIES.find((mt) => (groupedMeals[mt] || []).length > 0);
  const weekDays = getWeekDays();

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-32" data-testid="planner-page">
      <div className="pl-[72px] pr-5 pt-6 pb-4">
        <h1 className="font-heading text-3xl text-[#3D4035]">{t("mealPlannerTitle")}</h1>
      </div>

      {/* Week Nav */}
      <div className="px-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <button data-testid="planner-prev-week" onClick={() => { const d = new Date(currentDate); d.setHours(0,0,0,0); d.setDate(d.getDate() - 7); setCurrentDate(d); }} className="w-9 h-9 rounded-full bg-white border border-[#E8E5E0] flex items-center justify-center">
            <ChevronLeft className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
          </button>
          <button onClick={goToToday} className="font-body font-semibold text-[#3D4035] text-sm hover:text-[#7B9E7F] transition-colors">
            {currentDate.toLocaleDateString(lang === "uk" ? "uk-UA" : lang === "ru" ? "ru-RU" : "en-US", { month: "long", year: "numeric" })}
          </button>
          <button data-testid="planner-next-week" onClick={() => { const d = new Date(currentDate); d.setHours(0,0,0,0); d.setDate(d.getDate() + 7); setCurrentDate(d); }} className="w-9 h-9 rounded-full bg-white border border-[#E8E5E0] flex items-center justify-center">
            <ChevronRight className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex gap-1.5">
          {weekDays.map((day, idx) => {
            const ds = toLocalDateStr(day);
            const isSelected = ds === dateStr;
            const isToday = ds === toLocalDateStr();
            return (
              <button key={idx} data-testid={`planner-day-${idx}`} onClick={() => { const d = new Date(day); d.setHours(0,0,0,0); setCurrentDate(d); }} className={`flex-1 py-2.5 rounded-2xl flex flex-col items-center gap-0.5 transition-all ${
                isSelected ? "bg-[#7B9E7F] text-white shadow-md" : isToday ? "bg-[#7B9E7F]/10 text-[#4A6B4E]" : "bg-white text-[#6B705C] border border-[#E8E5E0]"
              }`}>
                <span className="font-body text-[9px] uppercase font-bold tracking-wider opacity-70">
                  {(dayNamesShort[lang] || dayNamesShort.uk)[day.getDay()]}
                </span>
                <span className="font-body text-base font-bold">{day.getDate()}</span>
              </button>
            );
          })}
        </div>
      </div>

      {(() => {
        const sub = user?.subscription || {};
        if (sub.access_override === true) return null;
        if (!isSubActive(user)) return null;
        const n = sub.planning_budget_days ?? 150;
        if (n > 30) return null;
        const color = n <= 5 ? "#BC4749" : n <= 15 ? "#E8973C" : "#6B7280";
        const label = plural(lang, n, {
          one: t("planDaysLeft_one"),
          few: t("planDaysLeft_few"),
          many: t("planDaysLeft_many"),
        }).replace("{n}", n);
        return (
          <div data-testid="plan-days-left" className="text-center text-sm mb-3" style={{ color }}>
            {label}
          </div>
        );
      })()}

      {/* Meals */}
      <div className="px-5">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#7B9E7F] animate-spin" /></div>
        ) : meals.length > 0 ? (
          <div className="space-y-5">
            {MEAL_CATEGORIES.map((mealType) => {
              const dishes = groupedMeals[mealType] || [];
              const undosForSection = Object.entries(undoItems).filter(([, m]) => m.meal_type === mealType);
              return (
                <div key={mealType} data-testid={`planner-section-${mealType}`}>
                  <div className="flex items-center justify-between mb-2 pl-1">
                    <p className="font-body text-xs text-[#A5A58D] uppercase tracking-wider font-bold">{t(mealType)}</p>
                    {dishes.length > 0 && (
                      <button
                        data-testid={`quick-add-${mealType}`}
                        onClick={() => openAddDish(mealType)}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EBE9E4] text-[#6B705C] font-body text-xs font-semibold hover:bg-[#E0DDD7] transition-colors"
                      >
                        <Plus className="w-3 h-3" strokeWidth={2} />
                      </button>
                    )}
                  </div>
                  {dishes.length === 0 ? (
                    <div className="bg-white/60 rounded-2xl border-2 border-dashed border-[#E8E5E0] p-6 text-center">
                      <p className="font-body text-sm text-[#A5A58D] mb-3">{t("noMealPlanned")}</p>
                      <button
                        data-testid={`add-dish-${mealType}`}
                        onClick={() => openAddDish(mealType)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#7B9E7F] text-white font-body text-sm font-semibold hover:bg-[#6B8E6F] transition-colors"
                      >
                        <Plus className="w-4 h-4" strokeWidth={2} />
                        {t("addDish")}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dishes.map((meal, i) => (
                        <div key={meal.recipe_id || `${mealType}-${i}`} data-testid={`planner-meal-${mealType}-${i}`} className={`rounded-2xl border shadow-sm overflow-hidden border-l-4 ${meal.status === "done" ? "bg-[#7B9E7F]/10 border-[#7B9E7F]/30 border-l-[#7B9E7F]" : `bg-white border-black/5 ${MEAL_BORDER_COLORS[meal.meal_type] || ""}`}`}>
                          {meal.status === "skipped" ? (
                            <div className="p-4 opacity-50">
                              <p className="font-body text-sm text-[#A5A58D] line-through">{t("skipped")}</p>
                            </div>
                          ) : meal.status === "done" ? (
                            /* Done/Cooked dish - warm green, no action buttons, but clickable */
                            <button
                              data-testid={`planner-done-${meal.recipe_id}`}
                              onClick={() => meal.recipe_id && navigate(`/recipe/${meal.recipe_id}?plan_id=${planId || ""}&date=${dateStr || ""}`)}
                              className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#7B9E7F]/15 transition-colors"
                            >
                              <div className="w-10 h-10 rounded-full bg-[#7B9E7F]/20 flex items-center justify-center shrink-0">
                                <Check className="w-5 h-5 text-[#7B9E7F]" strokeWidth={2} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-body font-semibold text-[#4A6B4E] truncate">{meal.recipe_title}</p>
                                <p className="font-body text-xs text-[#7B9E7F] mt-0.5">{t("cooked")}</p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-[#7B9E7F] shrink-0" strokeWidth={1.5} />
                            </button>
                          ) : (
                            <>
                              <button onClick={() => meal.recipe_id && navigate(`/recipe/${meal.recipe_id}?plan_id=${planId || ""}&date=${dateStr || ""}`)} className="w-full p-4 text-left hover:bg-[#FAFAF8] transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="font-body font-semibold text-[#3D4035]">{meal.recipe_title}</p>
                                  </div>
                                  <div className="flex flex-col items-end gap-1">
                                    {meal.calories && (
                                      <span className="flex items-center gap-1 text-xs font-body text-[#A5A58D]">
                                        <Flame className="w-3 h-3" strokeWidth={1.5} /> {meal.calories} {t("cal")}
                                      </span>
                                    )}
                                    {meal.cook_time && (
                                      <span className="flex items-center gap-1 text-xs font-body text-[#A5A58D]">
                                        <Clock className="w-3 h-3" strokeWidth={1.5} /> {meal.cook_time} {t("min")}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </button>
                              {/* Нижній ряд: три дії зліва, вартість справа — те саме, що на
                                  головній. Ціна не в стовпчику з калоріями, бо там вона додавала
                                  висоти клікабельній кнопці й роздувала картку.
                                  ml-auto замість justify-between принципово: justify-between
                                  розкидав би три кнопки по всій ширині замість щільної групи.
                                  ml-auto лишає їх укупі з gap-2 і тисне вправо саму лише ціну.
                                  flex-wrap прибрано свідомо: кнопки стали іконками без підписів,
                                  тож ряд вміщається навіть на 360px, а перенос кидав би ціну на
                                  власний рядок. shrink-0 на ціні добиває це — діапазон не
                                  стискається, коли три кнопки конкурують за ширину. */}
                              <div className="px-4 pb-3 flex items-center gap-2">
                                {/* Кнопки без підписів: три пігулки з текстом і діапазон в один
                                    рядок на 360px не вміщались. Підпис перенесено в title
                                    (підказка при наведенні) і aria-label (для зчитувача) —
                                    сама дія й далі названа тими самими ключами i18n. */}
                                <button
                                  data-testid={`planner-replace-${meal.meal_type}`}
                                  onClick={locked ? () => navigate("/subscription") : () => handleReplaceMeal(planId, meal.meal_type)}
                                  disabled={replacingMeal === meal.meal_type}
                                  title={t("replace")}
                                  aria-label={t("replace")}
                                  className="flex items-center justify-center gap-1 p-2 rounded-full bg-[#EBE9E4] text-[#6B705C] font-body text-xs font-semibold hover:bg-[#E0DDD7] transition-colors disabled:opacity-50"
                                >
                                  {replacingMeal === meal.meal_type ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" strokeWidth={1.5} />}
                                  {locked && <CrownIcon />}
                                </button>
                                <button
                                  onClick={() => setSkipModal(meal)}
                                  title={t("skipMeal")}
                                  aria-label={t("skipMeal")}
                                  className="flex items-center justify-center p-2 rounded-full bg-[#EBE9E4] text-[#6B705C] font-body text-xs font-semibold hover:bg-[#E0DDD7] transition-colors"
                                >
                                  <SkipForward className="w-3 h-3" strokeWidth={1.5} />
                                </button>
                                <button
                                  data-testid={`planner-remove-${meal.recipe_id}`}
                                  onClick={() => handleRemoveDish(meal)}
                                  title={t("removeDish")}
                                  aria-label={t("removeDish")}
                                  className="flex items-center justify-center p-2 rounded-full bg-[#EBE9E4] text-[#BC4749] font-body text-xs font-semibold hover:bg-[#BC4749]/10 transition-colors"
                                >
                                  <X className="w-3 h-3" strokeWidth={1.5} />
                                </button>
                                {formatPriceRange(
                                  dishCosts[meal.recipe_id]?.cost_min_total,
                                  dishCosts[meal.recipe_id]?.cost_max_total
                                ) && (
                                  <span className="flex items-center gap-1 ml-auto shrink-0 text-sm font-body font-semibold text-[#7B9E7F]">
                                    <Wallet className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    {formatPriceRange(
                                      dishCosts[meal.recipe_id]?.cost_min_total,
                                      dishCosts[meal.recipe_id]?.cost_max_total
                                    )}
                                  </span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {undosForSection.map(([recipeId, meal]) => (
                    <div key={`undo-${recipeId}`} data-testid={`planner-undo-${recipeId}`}
                      className="flex items-center justify-between px-4 py-2.5 mt-2 bg-[#EBE9E4]/60 rounded-xl border border-dashed border-[#A5A58D]/30">
                      <span className="font-body text-sm text-[#A5A58D]">{meal.recipe_title} — {t("removed")}</span>
                      <button onClick={() => handleUndo(meal)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#7B9E7F] font-body text-xs font-bold shadow-sm hover:shadow transition-all">
                        <Undo2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {t("undo")}
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="font-body text-[#6B705C] mb-4">{t("noMealsYet")}</p>
          </div>
        )}

        {genError && (
          <div data-testid="gen-error" className="text-[#BC4749] bg-[#BC4749]/10 rounded-xl px-4 py-3 mb-3 text-sm">
            {genError}
          </div>
        )}

        {/* Cook-time limit slider */}
        <CookTimeSlider valueIdx={cookTimeIdx} onChange={setCookTimeIdx} disabled={generating} className="mt-6 mb-4" />

        {/* Generate buttons */}
        <div className="mt-6 flex gap-2">
          <button data-testid="planner-generate-day" onClick={(locked && !(tutorialActive && tutorialStep === 1)) ? () => navigate("/subscription") : () => handleGenerate(1)} disabled={generating} className="flex-1 bg-[#7B9E7F] text-white rounded-full h-11 font-body font-bold hover:bg-[#6B8E6F] transition-all active:scale-[0.98] shadow-lg shadow-[#7B9E7F]/20 flex items-center justify-center gap-1.5 disabled:opacity-50 text-sm">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            {t("generateForDay")}
            {locked && !(tutorialActive && tutorialStep === 1) && <CrownIcon className="ml-1" />}
          </button>
          <button data-testid="planner-generate-3days" onClick={locked ? () => navigate("/subscription") : () => handleGenerate(3)} disabled={generating} className="flex-1 bg-[#EBE9E4] text-[#4A6B4E] rounded-full h-11 font-body font-bold hover:bg-[#E0DDD7] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 text-sm">
            {t("generateFor3Days")}
            {locked && <CrownIcon className="ml-1" />}
          </button>
          <button data-testid="planner-generate-week" onClick={locked ? () => navigate("/subscription") : () => handleGenerate(7)} disabled={generating} className="flex-1 bg-[#EBE9E4] text-[#4A6B4E] rounded-full h-11 font-body font-bold hover:bg-[#E0DDD7] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 text-sm">
            {t("generateForWeek")}
            {locked && <CrownIcon className="ml-1" />}
          </button>
        </div>

        {/* Generate from fridge button */}
        <div className="mt-2">
          <button onClick={locked ? () => navigate("/subscription") : handleGenerateFromFridge} disabled={generating} className="w-full bg-[#EBE9E4] text-[#4A6B4E] rounded-full h-11 font-body font-bold hover:bg-[#E0DDD7] transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 text-sm">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Refrigerator className="w-4 h-4" strokeWidth={1.5} />}
            {t("generateFromFridge") || "З наявних інгредієнтів"}
            {locked && <CrownIcon className="ml-1" />}
          </button>
        </div>

        {/* Tutorial Step 1 - Generate button */}
        <TutorialTooltip step={1} targetId="planner-generate-day" />

        {/* Tutorial Step 2 - First recipe card (state-based, no Next button) */}
        {firstFilledMealType && (
          <TutorialTooltip step={2} targetId={`planner-meal-${firstFilledMealType}-0`} />
        )}
      </div>

      {/* Skip Modal */}
      {skipModal && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl text-[#3D4035]">{t("skipMealTitle")}</h3>
              <button onClick={() => { setSkipModal(null); setSkipComment(""); }} className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center"><X className="w-4 h-4 text-[#6B705C]" /></button>
            </div>
            <p className="font-body text-sm text-[#6B705C] mb-3">{skipModal.recipe_title}</p>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setSkipComment(t("ateElsewhere"))} className={`px-4 py-2 rounded-full font-body text-sm font-semibold ${skipComment === t("ateElsewhere") ? "bg-[#7B9E7F] text-white" : "bg-white text-[#6B705C] border border-[#E8E5E0]"}`}>{t("ateElsewhere")}</button>
              <button onClick={() => setSkipComment(t("notHungry"))} className={`px-4 py-2 rounded-full font-body text-sm font-semibold ${skipComment === t("notHungry") ? "bg-[#7B9E7F] text-white" : "bg-white text-[#6B705C] border border-[#E8E5E0]"}`}>{t("notHungry")}</button>
            </div>
            <input type="text" value={skipComment} onChange={(e) => setSkipComment(e.target.value)} placeholder={t("skipReasonPlaceholder")} className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none mb-4" />
            <div className="flex gap-3">
              <button onClick={() => { setSkipModal(null); setSkipComment(""); }} className="flex-1 bg-[#EBE9E4] text-[#6B705C] rounded-full h-12 font-body font-bold">{t("cancel")}</button>
              <button onClick={() => handleSkipMeal(planId)} className="flex-1 bg-[#7B9E7F] text-white rounded-full h-12 font-body font-bold hover:bg-[#6B8E6F]">{t("confirmSkip")}</button>
            </div>
          </div>
        </div>
      )}

      <AddDishModal
        open={!!addDishModal}
        mealType={addDishModal?.mealType}
        planId={planId}
        date={dateStr}
        onClose={() => setAddDishModal(null)}
        onAdded={fetchPlans}
      />
    </div>
  );
}
